/**
 * Providers and Models: the assurance register (docs/plans/2026-10-assurance-assessments.md). Mojo Up rates each
 * provider and tier and corrects the register between releases from Mojo Up AI Cloud; an organisation records its
 * own assessment of a row (what its rating rests on, the evidence, a date to review it by), which stands over
 * Mojo Up's for its work, and adds providers the register lacks. Ratings feed a project's classification: what it
 * may use. On Cloud, Mojo Up's own organisation sees the register's editor instead.
 */

import { ASSURANCE_DESCRIPTIONS, ASSURANCE_ORDER, ASSURANCE_WORDS, type AssuranceAnswer, type AssuranceRow } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { Button } from '@heroui/react';
import { CircleCheck, Clock, Plus, ShieldCheck, Shield } from '@gravity-ui/icons';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { RatingMark, rowWord, tierWord } from '../components/assurance.tsx';
import { AddProviderDialog, AssessDialog, assessedLine, BasisLine, EditProviderDialog, EvidenceList, HistoryDialog, matchSentence, mojoUpRatingOf, RegisterRowDialog } from '../components/assuranceDialogs.tsx';
import { ArtEmpty, ArtStat, ConfirmDialog, ErrorNote, ListGroup, ListRow, Page, PageHeader, statNumber, StatusMark } from '../components/ui.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { timeAgo, useLoad } from '../hooks.ts';

const tone = VIEW_TONES.assurance!;
const key = (r: AssuranceRow) => `${r.provider}/${r.tier}`;

/** Where the register comes from, as a mark and one sentence. */
function registerSource(a: AssuranceAnswer): { state: 'connected' | 'idle' | 'error'; word: string; sentence: string } {
  const s = a.source;
  const reviewed = new Date(a.reviewedAt);
  const builtIn = `The register built into this release, reviewed by Mojo Up on ${Number.isNaN(reviewed.getTime()) ? a.reviewedAt : dateLong(reviewed)}`;
  if (!s) return { state: 'idle', word: 'Built in', sentence: `${builtIn}.` };
  const published = `revision ${s.revision}, published ${timeAgo(s.publishedAt)}`;
  if (s.kind === 'cloud' && s.publisher) return { state: 'connected', word: 'Publishing', sentence: `You publish this register (${published}).` };
  if (s.kind === 'cloud') return { state: 'connected', word: "Mojo Up's latest", sentence: `Mojo Up's register, ${published}.` };
  if (s.kind === 'enterprise-mirror') {
    if (s.lastError) return { state: 'error', word: 'Check failed', sentence: `Mojo Up's register, ${published}. The latest check could not fetch Mojo Up's updates: ${s.lastError}.` };
    return { state: 'connected', word: 'Up to date', sentence: `Mojo Up's register, ${published}${s.checkedAt ? `, checked ${timeAgo(s.checkedAt)}` : ''}.` };
  }
  if (s.lastError) return { state: 'error', word: 'Check failed', sentence: `${builtIn}; Mojo Up's updates could not be fetched: ${s.lastError}.` };
  return { state: 'idle', word: 'Built in', sentence: s.from ? `${builtIn}. Mojo Up's updates are fetched with each release check.` : `${builtIn}.` };
}

type Open =
  | { kind: 'assess'; row: AssuranceRow }
  | { kind: 'history'; row: AssuranceRow }
  | { kind: 'withdraw'; row: AssuranceRow }
  | { kind: 'add-provider' }
  | { kind: 'edit-provider'; row: AssuranceRow }
  | { kind: 'retire-provider'; row: AssuranceRow }
  | { kind: 'edit-row'; row?: AssuranceRow }
  | { kind: 'retire-row'; row: AssuranceRow };

export function AssurancePage() {
  const data = useLoad(() => routes.assurance(), []);
  const [open, setOpen] = useState<Open>();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string>();
  const a = data.data;
  const rows = a?.rows ?? [];
  const publisher = !!a?.canEditRegister;
  const canAssess = !!a?.canAssess;
  const mojoUp = rows.filter((r) => r.origin !== 'organisation');
  const own = rows.filter((r) => r.origin === 'organisation');
  const source = a ? registerSource(a) : undefined;
  const close = () => setOpen(undefined);
  const saved = () => (setOpen(undefined), data.reload());
  const act = async (fn: () => Promise<unknown>) => {
    try {
      setError(undefined);
      await fn();
      saved();
    } catch (e) {
      setError(problemText(e));
      setOpen(undefined);
    }
  };
  const check = async () => {
    try {
      setChecking(true);
      setError(undefined);
      await routes.checkAssurance();
      data.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setChecking(false);
    }
  };

  const actions = a ? (
    <>
      {a.canCheck ? (
        <Button size="sm" variant="secondary" isDisabled={checking} onPress={() => void check()}>
          {checking ? 'Checking…' : 'Check now'}
        </Button>
      ) : null}
      {canAssess ? (
        <Button size="sm" variant="primary" onPress={() => setOpen({ kind: 'add-provider' })}>
          <Plus className="size-4" />
          Add a provider
        </Button>
      ) : publisher ? (
        <Button size="sm" variant="primary" onPress={() => setOpen({ kind: 'edit-row' })}>
          <Plus className="size-4" />
          Add a row
        </Button>
      ) : null}
    </>
  ) : undefined;

  return (
    <Page>
      <PageHeader tone={tone} icon={<ShieldCheck />} title="Providers and Models" subtitle="How far each AI provider can be trusted with your data. A project's classification decides the lowest rating its work may use." actions={actions} />
      <PartBoundary key="assurance" name="Providers and Models" tone={tone}>
        <Explainer id="assurance" tone={tone} icon={<ShieldCheck />} title="How the register works" summary="Mojo Up rates each provider; your own assessment of a row stands over it for your work">
          <p>
            {publisher
              ? "You keep Mojo Up's register. A row you add, edit or retire here is published at once as a new revision: organisations on AI Cloud have it straight away, and each Enterprise deployment with its next release check. Organisations record their own assessments over it, and you never see theirs."
              : "Mojo Up reviews every row and corrects the register between releases; the corrections reach you with each release check. Where your own contract or deployment differs, choose Assess on a row and record what your rating rests on, with evidence and a date to review it by. For a provider the register lacks, such as your own Azure deployment, an internal gateway, a reseller or a model server on your premises, choose Add a provider. Your assessments are your organisation's alone."}
          </p>
          <ul className="m-0 grid list-none gap-2 p-0">
            {ASSURANCE_ORDER.map((r) => (
              <li key={r} className="grid gap-0.5 @[36rem]:grid-cols-[13rem_minmax(0,1fr)] @[36rem]:items-baseline @[36rem]:gap-3">
                <RatingMark rating={r} />
                <span>{ASSURANCE_DESCRIPTIONS[r]}</span>
              </li>
            ))}
          </ul>
        </Explainer>

        <ErrorNote error={error ?? data.error} />
        {a ? (
          <div className="art-stats">
            {publisher ? (
              <>
                <ArtStat tone={tone} icon={<ShieldCheck />} label="Rows published" value={statNumber(rows.length)} detail="to every deployment" />
                <ArtStat tone={tone} icon={<CircleCheck />} label="Changed since release" value={statNumber(rows.filter((r) => r.updatedAfterRelease).length)} detail="edited or added here" />
                <ArtStat tone={tone} icon={<Clock />} label="Retired" value={statNumber(a.retired?.length ?? 0)} detail="dropped from the register" />
                <ArtStat tone={tone} icon={<Shield />} label="Revision" value={a.source ? statNumber(a.source.revision) : '—'} detail={a.source ? `published ${timeAgo(a.source.publishedAt)}` : ''} />
              </>
            ) : (
              <>
                <ArtStat tone={tone} icon={<ShieldCheck />} label="Providers" value={statNumber(rows.length)} detail={`${statNumber(mojoUp.length)} rated by Mojo Up`} />
                <ArtStat tone={tone} icon={<CircleCheck />} label="Assessed by you" value={statNumber(a.counts?.assessed ?? 0)} detail={`of ${statNumber(rows.length)}`} />
                <ArtStat tone={tone} icon={<Clock />} label="Needs review" value={statNumber(a.counts?.reviewDue ?? 0)} detail={a.counts?.reviewDue ? 'past their review-by date' : 'nothing due'} needsYou={!!a.counts?.reviewDue} />
                <ArtStat tone={tone} icon={<Shield />} label="Your providers" value={statNumber(own.length)} detail="rated only by you" />
              </>
            )}
          </div>
        ) : null}


        {source ? (
          <ListGroup title="The register">
            <ListRow icon={<ShieldCheck />} title="Where it comes from" description={source.sentence} status={<StatusMark state={source.state} label={source.word} />} />
          </ListGroup>
        ) : null}

        <ListGroup
          title="Mojo Up's ratings"
          count={mojoUp.length || undefined}
          description={publisher ? 'What every deployment receives.' : 'Assess a row where your own contract or deployment is stronger or weaker than the general case.'}
          empty={
            mojoUp.length === 0 && !data.loading ? (
              <ArtEmpty tone={tone} icon={<ShieldCheck />} title="No register to show" compact>
                The register could not be read. Try again in a moment.
              </ArtEmpty>
            ) : undefined
          }
        >
          {mojoUp.map((r) => (
            <ProviderRow key={key(r)} row={r} publisher={publisher} canAssess={canAssess} onOpen={setOpen} />
          ))}
        </ListGroup>

        {canAssess ? (
          <ListGroup
            title="Your own providers"
            count={own.length || undefined}
            description="Providers the register does not have, rated by your organisation alone."
            empty={
              own.length === 0 ? (
                <ArtEmpty
                  tone={tone}
                  icon={<Shield />}
                  title="No providers of your own"
                  compact
                  action={
                    <Button size="sm" variant="secondary" onPress={() => setOpen({ kind: 'add-provider' })}>
                      Add a provider
                    </Button>
                  }
                >
                  Your own Azure deployment, an internal gateway or a model server on your premises: add it, say what work goes to it, and rate it.
                </ArtEmpty>
              ) : undefined
            }
          >
            {own.map((r) => (
              <ProviderRow key={key(r)} row={r} publisher={false} canAssess onOpen={setOpen} />
            ))}
          </ListGroup>
        ) : null}

        {publisher && a?.retired?.length ? (
          <ListGroup title="Retired rows" count={a.retired.length} description="Dropped from the register every deployment receives.">
            {a.retired.map((r) => (
              <ListRow
                key={key(r)}
                icon={<Clock />}
                title={
                  <>
                    {rowWord(r)} <span className="font-normal text-muted">· {tierWord(r.tier)}</span>
                  </>
                }
                description={r.note}
                compactControl
                control={
                  <Button size="sm" variant="secondary" onPress={() => setOpen({ kind: 'edit-row', row: r })}>
                    Bring back
                  </Button>
                }
              />
            ))}
          </ListGroup>
        ) : null}
      </PartBoundary>

      {open?.kind === 'assess' && <AssessDialog key={key(open.row)} row={open.row} canUpload={!!a?.canUploadEvidence} onClose={close} onSaved={saved} />}
      {open?.kind === 'history' && <HistoryDialog row={open.row} onClose={close} />}
      {open?.kind === 'add-provider' && <AddProviderDialog canUpload={!!a?.canUploadEvidence} onClose={close} onSaved={saved} />}
      {open?.kind === 'edit-provider' && <EditProviderDialog row={open.row} onClose={close} onSaved={saved} />}
      {open?.kind === 'edit-row' && <RegisterRowDialog {...(open.row ? { row: open.row } : {})} onClose={close} onSaved={saved} />}
      <ConfirmDialog isOpen={open?.kind === 'withdraw'} title="Use Mojo Up's rating?" confirmLabel="Withdraw assessment" onCancel={close} onConfirm={() => open?.kind === 'withdraw' && act(() => routes.withdrawAssessment(open.row.provider, open.row.tier))}>
        {open?.kind === 'withdraw' ? (
          <p>
            Your assessment of {rowWord(open.row)} stops counting, and Mojo Up's rating, {ASSURANCE_WORDS[mojoUpRatingOf(open.row)]}, applies to your organisation's work again. It stays in the row's history.
          </p>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog isOpen={open?.kind === 'retire-provider'} title="Retire this provider?" confirmLabel="Retire" danger onCancel={close} onConfirm={() => open?.kind === 'retire-provider' && act(() => routes.retireAssuranceProvider(open.row.provider, open.row.tier))}>
        {open?.kind === 'retire-provider' ? <p>{rowWord(open.row)} leaves your register, and work that went to it is rated as an unknown provider. Its assessments stay in the audit trail; adding it again under the same name brings them back.</p> : null}
      </ConfirmDialog>
      <ConfirmDialog isOpen={open?.kind === 'retire-row'} title="Retire this row from the register?" confirmLabel="Retire and publish" danger onCancel={close} onConfirm={() => open?.kind === 'retire-row' && act(() => routes.retireRegisterRow(open.row.provider, open.row.tier))}>
        {open?.kind === 'retire-row' ? (
          <p>
            {rowWord(open.row)}, {tierWord(open.row.tier)} is dropped from the register every deployment receives, as a new revision. You can bring it back from Retired Rows.
          </p>
        ) : null}
      </ConfirmDialog>
    </Page>
  );
}

/** One row: Mojo Up's rating and what it depends on; under it, the organisation's assessment when there is one. */
function ProviderRow({ row: r, publisher, canAssess, onOpen }: { row: AssuranceRow; publisher: boolean; canAssess: boolean; onOpen: (o: Open) => void }) {
  const own = r.origin === 'organisation';
  const menu: Array<{ id: string; label: string; danger?: boolean; run: () => void }> = publisher
    ? [{ id: 'retire', label: 'Retire from the register…', danger: true, run: () => onOpen({ kind: 'retire-row', row: r }) }]
    : [
        ...(r.assessments ? [{ id: 'history', label: 'History', run: () => onOpen({ kind: 'history', row: r }) }] : []),
        ...(own ? [{ id: 'edit', label: 'Change details', run: () => onOpen({ kind: 'edit-provider', row: r }) }] : []),
        ...(r.assessment && !own ? [{ id: 'withdraw', label: "Use Mojo Up's rating…", run: () => onOpen({ kind: 'withdraw', row: r }) }] : []),
        ...(own ? [{ id: 'retire', label: 'Retire…', danger: true, run: () => onOpen({ kind: 'retire-provider', row: r }) }] : []),
      ];
  // Mojo Up's rating is the row's mark, its reasons the row's words; the organisation's own assessment, when there
  // is one, stands under them. A provider of the organisation's own is rated by its assessment alone.
  const changed = !own && r.updatedAfterRelease ? `${publisher ? 'Added or changed here since this release was built' : 'Changed by Mojo Up since this release was built'}${r.releaseRating ? `; the release rates it ${ASSURANCE_WORDS[r.releaseRating]}.` : '.'}` : undefined;
  return (
    <ListRow
      title={
        <>
          {rowWord(r)} <span className="font-normal text-muted">· {tierWord(r.tier)}</span>
        </>
      }
      description={
        own ? (
          <>
            {r.match ? <span className="block">{matchSentence(r.match)}</span> : null}
            {r.note ? <span className="block">{r.note}</span> : null}
          </>
        ) : (
          <>
            <span className="block">{r.note}</span>
            {changed ? <span className="block">{changed}</span> : null}
          </>
        )
      }
      status={own ? <RatingMark rating={r.assessment?.rating ?? 'X'} /> : <RatingMark rating={mojoUpRatingOf(r)} />}
      compactControl
      control={
        <>
          {publisher ? (
            <Button size="sm" variant="secondary" onPress={() => onOpen({ kind: 'edit-row', row: r })}>
              Edit
            </Button>
          ) : canAssess ? (
            <Button size="sm" variant="secondary" onPress={() => onOpen({ kind: 'assess', row: r })}>
              Assess
            </Button>
          ) : null}
          <RowMenu label={`More for ${rowWord(r)}, ${tierWord(r.tier)}`} items={menu} onAction={(k) => menu.find((m) => m.id === k)?.run()} />
        </>
      }
    >
      {r.assessment ? (
        <div className="grid gap-1 rounded-lg bg-surface-secondary p-3">
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-xs font-medium">Your assessment</span>
            {own ? null : <RatingMark rating={r.assessment.rating} />}
            {r.reviewDue ? <StatusMark state="medium" label="Needs review" /> : null}
          </span>
          <BasisLine basis={r.assessment.basis} />
          {r.assessment.note ? <p className="m-0 text-xs text-muted">{r.assessment.note}</p> : null}
          <p className="m-0 text-xs text-muted">{assessedLine(r.assessment)}</p>
          <EvidenceList evidence={r.assessment.evidence} />
        </div>
      ) : own ? (
        <p className="m-0 text-xs text-muted">Not assessed, so it counts as Exploration only. Assess it to rate it.</p>
      ) : undefined}
    </ListRow>
  );
}
